import React, { useMemo, useRef } from 'react';
import styles from './Charts.module.scss';
import { EChartsOption } from 'echarts';
import ReactECharts from 'echarts-for-react';
import { ChartProps } from 'src/types/TCheckUsage';
import BarGraphShimmer from '../GraphShimmer';
import { sliceKeyForApiUsage } from 'src/utils/common';
import { useAlerts } from 'src/common/AlertManager';
import { Info } from 'src/common/icons';

function convertIntoMinutes(seconds: number): string {
  const roundedMinutes = Math.ceil(seconds / 60);
  return `${roundedMinutes}`;
}

const UnmemoizedChart: React.FC<ChartProps> = ({
  statsData,
  isLoading,
  hasFetched,
  graphTitle,
  valueInBytes = false,
  graphType,
  title,
  subtitle,
  suffixXAxis = false,
  gridStyle = false,
  valuesInMinutes = false,
  utcDates = false,
  emptyDataMsg = 'No Data Available',
  stat_type = '',
  stat_subtype = '',
  table_header_name = 'Date',
  hideXAxis = false,
}) => {

  const { showAlert } = useAlerts();
  const chartRef = useRef<any>(null);

  const handleDownloadImage = () => {
    const echartsInstance = chartRef.current?.getEchartsInstance();
    if (!echartsInstance && processedData.length === 0){
       showAlert("No Data to download as an Image")
       return
    }

    const dataURL = echartsInstance.getDataURL({
      type: 'png',
      pixelRatio: 2,
      backgroundColor: '#fff',
    });

    const baseFileName = graphTitle || title || 'chart-data';
    const suffix =
      stat_type && stat_subtype
        ? `-${stat_type}-${stat_subtype}`
        : stat_type
        ? `-${stat_type}`
        : stat_subtype
        ? `-${stat_subtype}`
        : '';

    const fileName = `${baseFileName}${suffix}.png`;

    const link = document.createElement('a');
    link.href = dataURL;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const processedData = useMemo(() => {
    if (!statsData || statsData.length === 0) return [];

    return statsData.map(({ timestamp, value }) => {
      let numericValue: number;
      let originalValue: string;

      if (typeof value === 'number') {
        numericValue = value;
        originalValue = String(value);
      } else {
        const match = value.match(/^([\d.]+)\s*(Bytes|KB|MB|GB|TB)?$/);
        if (!match) {
          return {
            date: typeof timestamp === 'number' ? new Date(timestamp * 1000).toLocaleTimeString() : timestamp,
            value: 0,
            original: value,
            fullPath: timestamp,
          };
        }
        const numVal = parseFloat(match[1]);
        const unit = match[2] || 'Bytes';
        const multipliers: Record<string, number> = {
          Bytes: 1,
          KB: 1024,
          MB: 1024 ** 2,
          GB: 1024 ** 3,
          TB: 1024 ** 4,
        };
        numericValue = numVal * (multipliers[unit] ?? 1);
        originalValue = value;
      }
      const formattedDate =
        typeof timestamp === 'number'
          ? new Date(timestamp * 1000).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'long',
              year: '2-digit',
              ...(utcDates ? { timeZone: 'UTC' } : {}),
            })
          : timestamp;
      return {
        date: formattedDate,
        value: numericValue,
        original: valuesInMinutes ? convertIntoMinutes(numericValue) : originalValue,
        fullPath: formattedDate,
      };
    });
  }, [statsData, valuesInMinutes, utcDates]);

  const option = useMemo(
    (): EChartsOption => ({
      grid: gridStyle ? { top: '10%' } : { top: '10%', bottom: '30%', left: '15%', right: '15%' },
      tooltip:
        processedData.length > 0
          ? ({
              trigger: 'axis',
              axisPointer: {
                type: 'shadow',
                shadowStyle: {
                  color: 'rgba(66, 85, 255, 0.05)',
                },
              },
              textStyle: { fontFamily: 'Ubuntu', fontSize: 14 },
              formatter: (params: any[]) =>
                params
                  .map((p) => {
                    const item = processedData[p.dataIndex];
                    const label = suffixXAxis && !gridStyle ? item.fullPath : item.date;
                    return `${p.marker} ${label}: ${item.original}`;
                  })
                  .join('<br/>'),
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              borderColor: '#E5E7EB',
              borderWidth: 1,
              padding: [8, 12],
              extraCssText: 'box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15); border-radius: 4px;',
            } as any)
          : {},
      xAxis: {
        type: 'category',
        boundaryGap: graphType === 'line' ? false : true,
        data:
          processedData.length > 0
            ? processedData.map((item) => (suffixXAxis ? sliceKeyForApiUsage(item.date) : item.fullPath))
            : [''],
        axisLabel: {
          show: processedData.length > 0 && !hideXAxis,
          formatter: (value: string) => value,
          fontFamily: 'Ubuntu',
          fontSize: 10,
          interval: 0,
          rotate: 30,
          padding: 2,
        },
        splitLine: {
          show: graphType === 'line',
          lineStyle: {
            color: 'rgba(21, 22, 22, 0.8)',
            type: 'dashed',
          },
        },
        axisLine: {
          lineStyle: {
            color: 'rgba(21, 22, 22, 0.8)',
          },
        },
      },
      yAxis: {
        type: 'value',
        axisLine: {
          show: true,
          lineStyle: {
            color: 'rgba(21, 22, 22, 0.8)',
          },
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: 'rgba(21, 22, 22, 0.8)',
            type: 'dashed',
          },
        },
        axisLabel: {
          show: processedData.length > 0,
          fontFamily: 'Ubuntu',
          fontSize: 10,
          formatter: (val: number): string => {
            if (valuesInMinutes) {
              return convertIntoMinutes(val);
            } else if (valueInBytes) {
              if (val >= 1e12) return (val / 1e12).toFixed(2) + ' TB';
              if (val >= 1e9) return (val / 1e9).toFixed(2) + ' GB';
              if (val >= 1e6) return (val / 1e6).toFixed(2) + ' MB';
              if (val >= 1e3) return (val / 1e3).toFixed(2) + ' KB';
              return val + ' Bytes';
            }
            return val.toString();
          },
        },
      },
      series:
        processedData.length > 0
          ? [
              {
                name: 'Usage',
                type: graphType as 'bar' | 'line',
                barWidth: statsData && statsData.length === 1 ? 50 : '30%',
                data: processedData.map((item) => item.value),
                itemStyle: {
                  color: '#4255FF',
                  borderRadius: graphType === 'bar' ? [6, 6, 6, 6] : 0,
                  shadowColor: 'rgba(66, 85, 255, 0.3)',
                  shadowBlur: 10,
                  shadowOffsetY: 5,
                },
                lineStyle:
                  graphType === 'line'
                    ? {
                        width: 3,
                        color: '#4255FF',
                        shadowColor: 'rgba(66, 85, 255, 0.3)',
                        shadowBlur: 10,
                      }
                    : undefined,
                symbol: graphType === 'line' ? 'circle' : 'none',
                symbolSize: 8,
                smooth: graphType === 'line' ? 0.3 : false,
                areaStyle:
                  graphType === 'line'
                    ? {
                        color: {
                          type: 'linear',
                          x: 0,
                          y: 0,
                          x2: 0,
                          y2: 1,
                          colorStops: [
                            { offset: 0, color: 'rgba(66, 85, 255, 0.3)' },
                            { offset: 1, color: 'rgba(66, 85, 255, 0.05)' },
                          ],
                        },
                        shadowColor: 'rgba(66, 85, 255, 0.2)',
                        shadowBlur: 10,
                      }
                    : undefined,
                label: {
                  show: true, // this is essential for downloading the graph with data points
                  position: 'top',
                  fontFamily: 'Ubuntu',
                  fontSize: 10,
                  formatter: ({ dataIndex }: { dataIndex: number }) => processedData[dataIndex].original,
                },
                emphasis: {
                  itemStyle: {
                    color: '#3344FF',
                    shadowBlur: 20,
                    shadowColor: 'rgba(66, 85, 255, 0.5)',
                  },
                },
              },
            ]
          : [],
      graphic:
        !isLoading && processedData && processedData.length === 0
          ? [
              {
                type: 'text',
                left: 'center',
                top: 'center',
                style: {
                  text: emptyDataMsg,
                  fontSize: 16,
                  fontWeight: 'bold',
                  fill: '#999',
                  fontFamily: 'Ubuntu',
                },
              },
            ]
          : [],
    }),
    [processedData, graphTitle, valueInBytes, valuesInMinutes, graphType, suffixXAxis, gridStyle, hideXAxis],
  );

  if (isLoading && !hasFetched) {
    return <BarGraphShimmer barColor="#d3d3d3" animationDuration={2} />;
  }

  return (
    <div className={styles.chartWrapper}>
      <div className={styles.chartContainer}>
        <div className={styles.chartHeader}>
          <div className={styles.headerText}>
            <p className={styles.title}>{title}</p>
            <p className={styles.subtitle}>{subtitle}</p>
          </div>
        </div>

        <ReactECharts
          key={`chart-${JSON.stringify(processedData)}`}
          option={option}
          ref={chartRef}
          opts={{ renderer: 'canvas' }}
          style={{ height: '400px', width: '100%' }}
        />
      </div>
    </div>
  );
};

export const ChartComponent = React.memo(UnmemoizedChart);
